import { useState, useEffect, useRef } from 'react'

import type _Account from 'mobx/Account'
import type { AuthenticatorStatusKeys } from 'mobx/Account/Account.type'
import type _Application from 'mobx/Application'
import type _Infra from 'mobx/Infra'

import oStorage from 'utils/o-storage'

export const AUTHENTICATOR_STATUS = {
    UNSTARTED: 'unstarted',
	DISCONNECTED: 'disconnected',
	WAITING: 'waiting',
	CONNECTING: 'connecting',
	CONNECTED: 'connected',
	EXPIRED: 'expired',
}

// Exported so the page-level coordinator (`pages/Account.tsx`) can introspect the
// persisted connection state without going through the hook (it needs to decide
// whether to honor an incoming `qrToken` *before* the hook mounts, and we don't
// want to fire a PATCH that would just clobber an already-live `connected` entry).
export const AUTHENTICATOR_STORAGE_KEY = 'authenticatorConnectionState'

interface KioskQrBootstrap {
	qrToken: string
	// `undefined` means the page-level PATCH /v3/auth/QR/:qrToken hasn't resolved yet; the call site
	// must gate `init()` until this becomes a definitive boolean so we don't conflate "still waiting"
	// with "PATCH failed" and surface an erroneous "scan again" before the kiosk session is bound.
	patchOk: boolean | undefined
	keys?: AuthenticatorStatusKeys
}

const useAuthenticator = (
	Application: typeof _Application,
	Account: typeof _Account,
	onDisconnection?: () => void,
	kioskQrBootstrap?: KioskQrBootstrap,
) => {
    const initialCodeExpiresDuration = 60
    const factor = 100 / initialCodeExpiresDuration

    const statusDisconnectedPollingTime = 4000
    const statusConnectedPollingTime = 15000
    const statusConnectingPollingTime = 1000
    const defaultConnectionState = {
        status: AUTHENTICATOR_STATUS.UNSTARTED,
        keys: null,
        code: null,
    }
    const savedConnectionState: { status: string; keys: AuthenticatorStatusKeys; code: string, lastSave: number } = oStorage.get(AUTHENTICATOR_STORAGE_KEY)

    const [codeExpiresIn, setCodeExpiresIn] = useState(initialCodeExpiresDuration)
    const [progress, setProgress] = useState(100)
    const [connectionState, setConnectionState] = useState(savedConnectionState || defaultConnectionState)
    const [unknownExpirationTime, setUnknownExpirationTime] = useState(connectionState.status === AUTHENTICATOR_STATUS.WAITING)
    const [scanAgain, setScanAgain] = useState(false)

    const initied = useRef(false)
    const statusIsFetching = useRef(false)
    const askedDisconnection = useRef(false)
    const timerRef = useRef<NodeJS.Timer | null>(null)
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null)
    // The kiosk-initiated bootstrap is keyed by `qrToken` rather than being a one-shot boolean.
    //
    // Why per-token (not "ever bootstrapped"):
    // - Disconnect-dialog dismiss → `init()` re-entry with the *same* `kioskQrBootstrap.qrToken`
    //   must NOT re-arm the now-disconnected kiosk session (that's the original loop bug). Same
    //   token === skip the bootstrap branch and let `handleStatus → start()` regenerate a fresh
    //   waiting QR.
    // - But a *new* `qrToken` arriving while we're still mounted (e.g. user scans a second kiosk
    //   QR after the first one closed) is a legitimate fresh bootstrap. A naive boolean would
    //   leave us polling the previous session's keys until they EXPIRE, after which the EXPIRED
    //   arm (gated by `patchOk === true`) calls `stopAndKillToken()` and the QR renders as a
    //   black square.
    const lastBootstrappedTokenRef = useRef<string | null>(null)
    // The "previously generated code is still valid" copy is meant for the rehydration case:
    // we loaded a WAITING entry from `localStorage` on first mount and don't know how much of the
    // 60s window is left. Once the user has interacted (e.g. dismissed a disconnect dialog, which
    // re-enters `init()` after the inner `start()` has freshly generated a new code), we should
    // trust the explicit `setUnknownExpirationTime(false)` from `start()` and show the countdown.
    const initialMountRef = useRef(true)

    const isConnected = connectionState.status === AUTHENTICATOR_STATUS.CONNECTED
    const isConnecting = connectionState.status === AUTHENTICATOR_STATUS.CONNECTING

    // Timer to handle the authenticator status polling
    useEffect(() => {
        if (initied.current && connectionState.keys && connectionState.lastSave) {
            const pollMs = isConnected
                ? statusConnectedPollingTime
                : isConnecting
                    ? statusConnectingPollingTime
                    : statusDisconnectedPollingTime
            statusTimerRef.current = setTimeout(
                async () => {
                    await handleStatus()
                },
                pollMs
            )
        }

        return () => {
            clearTimeout(statusTimerRef.current as NodeJS.Timeout)
        }
    }, [initied.current, connectionState.keys, connectionState.lastSave])

    // Timer to handle the displayed expiration time
    useEffect(() => {
        if (initied.current && connectionState.code && unknownExpirationTime === false) {
            timerRef.current = setInterval(() => {
                setCodeExpiresIn((oldCodeExpiresIn) => oldCodeExpiresIn - 1)
            }, 1000)
        }

        return () => {
            clearInterval(timerRef.current as NodeJS.Timer)
        }
    }, [initied.current, connectionState.code, unknownExpirationTime])

    // Progress bar animation and restart timer handler
    useEffect(() => {
        setProgress(codeExpiresIn * factor)
        if (codeExpiresIn === 0) {
            stop()
            handleStatus()
        }
    }, [codeExpiresIn])

    useEffect(() => {
        if(connectionState.status === AUTHENTICATOR_STATUS.DISCONNECTED && askedDisconnection.current === false) {
            if(onDisconnection !== undefined) {
                onDisconnection()
            }
        }
    }, [connectionState.status])

    const init = async () => {
        initied.current = true
        const wasInitialMount = initialMountRef.current
        initialMountRef.current = false

        // Only enter the kiosk-bootstrap branches when this `qrToken` hasn't been bootstrapped
        // yet on this hook instance. Same-token re-entries (disconnect-ack `okAction` → `init()`)
        // fall through to `handleStatus()` and let it regenerate a waiting QR via inner
        // `start()`; new-token entries (user scanned a different deeplink) re-run the kiosk
        // bootstrap with the freshly PATCHed keys.
        const isFreshKioskToken =
            !!kioskQrBootstrap?.qrToken && kioskQrBootstrap.qrToken !== lastBootstrappedTokenRef.current

        // Kiosk-initiated QR login: identity has already been bound via PATCH /v3/auth/QR/:qrToken
        // (owned by the page). We bootstrap directly into 'connecting' and start polling so the UI
        // surfaces "Connecting…" until the kiosk completes the connect action.
        if (isFreshKioskToken && kioskQrBootstrap!.patchOk && kioskQrBootstrap!.keys) {
            lastBootstrappedTokenRef.current = kioskQrBootstrap!.qrToken
            save({
                status: AUTHENTICATOR_STATUS.CONNECTING,
                keys: kioskQrBootstrap!.keys,
                code: null,
            })
            setUnknownExpirationTime(true)
            return
        }

        // Kiosk-initiated QR login but the PATCH failed (404 expired / 409 already bound):
        // surface "Scan again" and fall through to the regular waiting QR flow.
        if (isFreshKioskToken && kioskQrBootstrap!.patchOk === false) {
            lastBootstrappedTokenRef.current = kioskQrBootstrap!.qrToken
            setScanAgain(true)
            await start()
            return
        }

        const status = await handleStatus()
        // Only the very first `init()` after mount is a true rehydration of `localStorage`-saved
        // state where the time-left for a WAITING code is unknown. Subsequent `init()` calls
        // (disconnect-ack → `okAction`) follow a fresh inner `start()` that explicitly resets
        // `unknownExpirationTime` to false, and we must not stomp it back to true here just
        // because the server reports the new session as 'waiting'.
        if (wasInitialMount && status === AUTHENTICATOR_STATUS.WAITING) {
            setUnknownExpirationTime(true)
        }
    }

    const save = (connectionState: any) => {
        const lastSave = new Date().getTime()
        const lastConnectionState = {
            ...connectionState,
            lastSave,
        }

        // Save the connection state at component level
        setConnectionState(lastConnectionState)

        // Save the connection state in browser memory
        oStorage.set(AUTHENTICATOR_STORAGE_KEY, lastConnectionState)
    }

    const stop = () => {
        clearInterval(timerRef.current as NodeJS.Timer)
        clearTimeout(statusTimerRef.current as NodeJS.Timeout)
    }

    // Stop all polling/timers and clear the persisted token. Call sites: Close QR overlay,
    // Global timer expiry, Global scanner kicks the user out — we don't want a stale token
    // to keep ticking in the background.
    const stopAndKillToken = () => {
        stop()
        save(defaultConnectionState)
        setCodeExpiresIn(initialCodeExpiresDuration)
        setUnknownExpirationTime(false)
        setScanAgain(false)
    }

    const start = async () => {
        reset()

        const { code, waitingKey, connectedKey } = await getCode()

        save({
            status: AUTHENTICATOR_STATUS.WAITING,
            keys: { waitingKey, connectedKey },
            code,
        })

        setUnknownExpirationTime(false)
        setCodeExpiresIn(initialCodeExpiresDuration)
    }

    const reset = () => {
        save(defaultConnectionState)
        stop()
        setCodeExpiresIn(initialCodeExpiresDuration)
    }

    const disconected = () => {
        save({
            keys: null,
            status: AUTHENTICATOR_STATUS.DISCONNECTED,
            code: null,
        })
        stop()
        setCodeExpiresIn(initialCodeExpiresDuration)
    }

    const handleStatus = async () => {
        if (!connectionState.keys) {
            start()
            return
        }

        if (statusIsFetching.current === true) {
            return
        }

        const status = await getStatus(connectionState.keys)

        if (askedDisconnection.current === true) {
            return
        }

        if (
            status === AUTHENTICATOR_STATUS.WAITING ||
            status === AUTHENTICATOR_STATUS.CONNECTING ||
            status === AUTHENTICATOR_STATUS.CONNECTED
        ) {
            save({
                ...connectionState,
                status,
            })
        }

        if (status === AUTHENTICATOR_STATUS.EXPIRED) {
            // Only the success path (PATCH bound this user to the kiosk session) is truly
            // "kiosk-owned": its expiry is terminal and the user must rescan from the kiosk.
            // The failure-fallback path (`patchOk === false`) committed to a regular waiting
            // session via inner `start()` above; its expiry is just the normal 60s rotation
            // and we should regenerate the code, not kill it (otherwise `connectionState.code`
            // goes back to `null` and the QR renders as an empty black square).
            if (kioskQrBootstrap?.patchOk === true) {
                // Order matters: `stopAndKillToken()` resets `scanAgain` to false as part of
                // its full reset, so we must surface "scan again" *after* it, not before.
                stopAndKillToken()
                setScanAgain(true)
            } else {
                start()
            }
        }

        if (status === AUTHENTICATOR_STATUS.DISCONNECTED) {
            disconected()
        }

        return status
    }

    const getCode = async () => Account.getAuthenticatorCode(Application.backendChannel)

    const getStatus = async (keys: AuthenticatorStatusKeys): Promise<string> => {
        statusIsFetching.current = true
        const status = await Account.getAuthenticatorStatus(Application.backendChannel, keys)
        statusIsFetching.current = false

        return status
    }

    const disconnect = async (): Promise<void> => {
        askedDisconnection.current = true
        await Account.disconnectAuthenticator(Application.backendChannel, connectionState.keys)
        askedDisconnection.current = false
        await start()
    }

    return {
        isConnected,
        isConnecting,
        connectionState,
        progress,
        unknownExpirationTime,
        codeExpiresIn,
        scanAgain,
        start: init,
        disconnect,
        stopAndKillToken,
    }
}

export default useAuthenticator
